import { useCallback } from 'react';
import LogicFlow from '@logicflow/core';

interface UseOPMViewControlProps {
  lfRef: React.RefObject<LogicFlow | null>;
  canvasRef: React.RefObject<HTMLDivElement | null>;
  setSavedViewState: (state: any) => void;
  savedViewState?: any;
}

export const useOPMViewControl = ({
  lfRef,
  canvasRef,
  setSavedViewState,
  savedViewState
}: UseOPMViewControlProps) => {
  // 放大画布
  const handleZoomIn = useCallback(() => {
    if (!lfRef.current) return;

    console.log('放大画布');

    // 获取当前缩放比例
    const transform = lfRef.current.getTransform() as any;
    const currentScale = transform?.SCALE_X || 1;

    // 放大1.2倍，但不超过3倍
    const newScale = Math.min(currentScale * 1.2, 3);
    lfRef.current.zoom(newScale);
  }, [lfRef]);

  // 缩小画布
  const handleZoomOut = useCallback(() => {
    if (!lfRef.current) return;

    console.log('缩小画布');

    // 获取当前缩放比例
    const transform = lfRef.current.getTransform() as any;
    const currentScale = transform?.SCALE_X || 1;

    // 缩小1.2倍，但不小于0.1倍
    const newScale = Math.max(currentScale / 1.2, 0.1);
    lfRef.current.zoom(newScale);
  }, [lfRef]);

  // 适应画布大小
  const handleFitView = useCallback(() => {
    if (!lfRef.current) return;

    console.log('适应画布大小');

    // 获取所有节点的边界
    const graphData = lfRef.current.getGraphData() as {
      nodes: any[];
      edges: any[];
    };
    if (!graphData.nodes || graphData.nodes.length === 0) {
      return;
    }

    // 计算所有节点的边界框
    let minX = Infinity,
      minY = Infinity,
      maxX = -Infinity,
      maxY = -Infinity;

    graphData.nodes.forEach((node: any) => {
      const x = node.x || 0;
      const y = node.y || 0;
      const width = node.width || 100;
      const height = node.height || 60;

      minX = Math.min(minX, x - width / 2);
      maxX = Math.max(maxX, x + width / 2);
      minY = Math.min(minY, y - height / 2);
      maxY = Math.max(maxY, y + height / 2);
    });

    // 如果没有有效的边界，使用默认值
    if (
      !isFinite(minX) ||
      !isFinite(minY) ||
      !isFinite(maxX) ||
      !isFinite(maxY)
    ) {
      lfRef.current.resetZoom();
      lfRef.current.resetTranslate();
      return;
    }

    // 计算内容的中心点和尺寸
    const contentWidth = maxX - minX;
    const contentHeight = maxY - minY;
    const centerX = (minX + maxX) / 2;
    const centerY = (minY + maxY) / 2;

    // 获取画布尺寸
    const canvasElement = canvasRef.current;
    if (!canvasElement) return;

    const canvasWidth = canvasElement.offsetWidth || 800;
    const canvasHeight = canvasElement.offsetHeight || 600;

    // 计算合适的缩放比例（留一些边距）
    const padding = 50;
    const scaleX = (canvasWidth - padding * 2) / contentWidth;
    const scaleY = (canvasHeight - padding * 2) / contentHeight;
    const scale = Math.min(scaleX, scaleY, 2); // 最大不超过2倍

    // 应用缩放和居中
    lfRef.current.zoom(scale);

    // 计算平移量使内容居中
    const translateX = canvasWidth / 2 - centerX * scale;
    const translateY = canvasHeight / 2 - centerY * scale;
    lfRef.current.translate(translateX, translateY);
  }, [lfRef, canvasRef]);

  // 复位视图
  const handleResetView = useCallback(() => {
    if (!lfRef.current) return;

    console.log('复位视图并居中显示内容');

    // 重置缩放和平移
    lfRef.current.resetZoom();
    lfRef.current.resetTranslate();

    // 清除选择
    lfRef.current.clearSelectElements();

    // 获取所有节点并居中显示
    const graphData = lfRef.current.getGraphData() as {
      nodes: any[];
      edges: any[];
    };
    
    if (graphData.nodes && graphData.nodes.length > 0) {
      // 计算所有节点的中心点
      let totalX = 0, totalY = 0;
      const nodeCount = graphData.nodes.length;

      graphData.nodes.forEach((node: any) => {
        totalX += node.x || 0;
        totalY += node.y || 0;
      });

      const centerX = totalX / nodeCount;
      const centerY = totalY / nodeCount;

      // 获取画布尺寸
      const canvasElement = canvasRef.current;
      if (canvasElement) {
        const canvasWidth = canvasElement.offsetWidth || 800;
        const canvasHeight = canvasElement.offsetHeight || 600;

        // 计算平移量使内容中心与画布中心对齐
        const translateX = canvasWidth / 2 - centerX;
        const translateY = canvasHeight / 2 - centerY;
        
        // 应用平移
        lfRef.current.translate(translateX, translateY);
        
        console.log('内容已居中显示:', { centerX, centerY, translateX, translateY });
      }
    }
  }, [lfRef, canvasRef]);

  // 保存视图状态
  const handleSaveViewState = useCallback(() => {
    if (!lfRef.current) return;

    const transform = lfRef.current.getTransform();
    setSavedViewState(transform);
    console.log('保存视图状态:', transform);
  }, [lfRef, setSavedViewState]);

  // 还原视图状态
  const handleRestoreViewState = useCallback(() => {
    if (!lfRef.current || !savedViewState) return;

    console.log('还原视图状态:', savedViewState);

    // LogicFlow 可能没有直接的 setTransform 方法，使用其他方式
    // 这里可能需要根据实际的 LogicFlow API 调整
    try {
      lfRef.current.zoom(savedViewState?.scale || 1);
      lfRef.current.translate(
        savedViewState?.translateX || 0,
        savedViewState?.translateY || 0
      );
    } catch (error) {
      console.warn('还原视图状态失败:', error);
    }
  }, [lfRef, savedViewState]);

  return {
    handleZoomIn,
    handleZoomOut,
    handleFitView,
    handleResetView,
    handleSaveViewState,
    handleRestoreViewState
  };
};